import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { 
  Save, 
  X, 
  Target, 
  TrendingUp,
  Users,
  Briefcase
} from 'lucide-react';
import { api } from '../services/api';

const NewSkill = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    category: 'Frontend',
    description: '',
    level: 'Intermediate',
    peopleCount: 0,
    projectDemand: 0,
    gap: 0,
    trend: 'stable'
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Create skill data
    const skillData = {
      id: `skill_${Date.now()}`,
      ...formData,
      peopleCount: parseInt(formData.peopleCount) || 0,
      projectDemand: parseInt(formData.projectDemand) || 0,
      gap: parseInt(formData.gap) || 0,
      createdAt: new Date().toISOString()
    };

    // Save to server Neo4j database & localStorage
    try {
      await api.createSkill(skillData);
      console.log('Skill saved to Neo4j database:', skillData);
    } catch (err) {
      console.warn('Fallback: saved locally due to error:', err);
    }
    
    // Navigate back to skills page
    navigate('/skills');
  };

  const categories = [
    'Frontend', 'Backend', 'Design', 'Database', 
    'AI/ML', 'Infrastructure', 'Mobile', 'Testing',
    'DevOps', 'Security', 'Data Science', 'Other'
  ];

  const levels = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];
  
  const trends = ['up', 'down', 'stable'];

  return (
    <div className={`min-h-screen p-6 transition-colors duration-200 ${
      isDarkMode ? 'bg-dark-900 text-white' : 'bg-light-50 text-light-950'
    }`}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">Add New Skill</h1>
            <p className={`mt-2 transition-colors duration-200 ${
              isDarkMode ? 'text-dark-300' : 'text-light-600'
            }`}>
              Define a new skill for your team's skill inventory
            </p>
          </div>
          
          <button
            onClick={() => navigate('/skills')}
            className={`p-2 rounded-lg transition-colors duration-200 ${
              isDarkMode 
                ? 'hover:bg-dark-800 text-dark-300 hover:text-white' 
                : 'hover:bg-light-200 text-light-600 hover:text-light-900'
            }`}
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information */}
          <div className={`card ${isDarkMode ? 'dark' : 'light'}`}>
            <h2 className="text-xl font-semibold mb-6 flex items-center">
              <Target className="mr-3 text-primary-500" size={20} />
              Basic Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Skill Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className={`input-field w-full transition-all duration-200 ${
                    isDarkMode ? 'dark' : 'light'
                  }`}
                  placeholder="e.g., React, Python, UI/UX Design"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Category *
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className={`input-field w-full transition-all duration-200 ${
                    isDarkMode ? 'dark' : 'light'
                  }`}
                >
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2">
                  Description *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={3}
                  className={`input-field w-full transition-all duration-200 ${
                    isDarkMode ? 'dark' : 'light'
                  }`}
                  placeholder="Describe what this skill involves and its applications..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Skill Level *
                </label>
                <select
                  name="level"
                  value={formData.level}
                  onChange={handleChange}
                  className={`input-field w-full transition-all duration-200 ${
                    isDarkMode ? 'dark' : 'light'
                  }`}
                >
                  {levels.map(level => (
                    <option key={level} value={level}>
                      {level}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Trend *
                </label>
                <select
                  name="trend"
                  value={formData.trend}
                  onChange={handleChange}
                  className={`input-field w-full transition-all duration-200 ${
                    isDarkMode ? 'dark' : 'light'
                  }`}
                >
                  {trends.map(trend => (
                    <option key={trend} value={trend}>
                      {trend.charAt(0).toUpperCase() + trend.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Demand Analysis */}
          <div className={`card ${isDarkMode ? 'dark' : 'light'}`}>
            <h2 className="text-xl font-semibold mb-6 flex items-center">
              <TrendingUp className="mr-3 text-green-500" size={20} />
              Demand Analysis
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2">
                  <Users className="inline mr-2" size={16} />
                  People with this skill
                </label>
                <input
                  type="number"
                  name="peopleCount"
                  value={formData.peopleCount}
                  onChange={handleChange}
                  min="0"
                  className={`input-field w-full transition-all duration-200 ${
                    isDarkMode ? 'dark' : 'light'
                  }`}
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  <Briefcase className="inline mr-2" size={16} />
                  Project demand
                </label>
                <input
                  type="number"
                  name="projectDemand"
                  value={formData.projectDemand}
                  onChange={handleChange}
                  min="0"
                  className={`input-field w-full transition-all duration-200 ${
                    isDarkMode ? 'dark' : 'light'
                  }`}
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  <Target className="inline mr-2" size={16} />
                  Skills gap
                </label>
                <input
                  type="number"
                  name="gap"
                  value={formData.gap}
                  onChange={handleChange}
                  className={`input-field w-full transition-all duration-200 ${
                    isDarkMode ? 'dark' : 'light'
                  }`}
                  placeholder="0"
                />
                <p className="text-xs mt-1 opacity-75">
                  Positive = shortage, Negative = surplus
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/skills')}
              className={`px-6 py-3 rounded-lg font-medium transition-colors duration-200 ${
                isDarkMode 
                  ? 'bg-dark-700 hover:bg-dark-600 text-white' 
                  : 'bg-light-200 hover:bg-light-300 text-light-900'
              }`}
            >
              Cancel
            </button>
            
            <button
              type="submit"
              className="btn-primary flex items-center"
            >
              <Save className="mr-2" size={16} />
              Save Skill
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewSkill;
